import { useEffect, useRef, useState, type PointerEvent, type TouchEvent } from 'react';
import { CalendarDays, Download, Filter, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/Sheet';
import {
  DaySheetlyFiltersComponent,
  formatDaySheetlyRange,
  type DaySheetlyFilters,
} from './DaySheetlyFilters';

interface FilterOption {
  id: string;
  name: string;
}

interface MobileDaySheetlyFiltersProps {
  filters: DaySheetlyFilters;
  defaultDateRange: Pick<DaySheetlyFilters, 'startDate' | 'endDate'>;
  onChange: (filters: DaySheetlyFilters) => void;
  projects: FilterOption[];
  onImport: () => void;
  onExport: () => void;
}

const DRAG_CLOSE_THRESHOLD = 64;

export function MobileDaySheetlyFilters({
  filters,
  defaultDateRange,
  onChange,
  projects,
  onImport,
  onExport,
}: MobileDaySheetlyFiltersProps) {
  const [open, setOpen] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(filters);
  const [popoverContainer, setPopoverContainer] = useState<HTMLDivElement | null>(null);
  const dragStartY = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setDraftFilters(filters);
    }
  }, [filters, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftFilters(filters);
      setOpen(true);
      return;
    }

    setOpen(false);
    onChange(draftFilters);
  };

  const visibleFilters = open ? draftFilters : filters;
  const dateLabel =
    visibleFilters.startDate && visibleFilters.endDate
      ? formatDaySheetlyRange(visibleFilters.startDate, visibleFilters.endDate)
      : 'Select date range';

  const closeFromDrag = (currentY: number | undefined) => {
    if (
      dragStartY.current !== null &&
      currentY !== undefined &&
      currentY - dragStartY.current > DRAG_CLOSE_THRESHOLD
    ) {
      handleOpenChange(false);
    }
    dragStartY.current = null;
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    dragStartY.current = event.clientY;
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (
      dragStartY.current !== null &&
      event.clientY - dragStartY.current > DRAG_CLOSE_THRESHOLD
    ) {
      handleOpenChange(false);
      dragStartY.current = null;
    }
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    dragStartY.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    const currentY = event.touches[0]?.clientY;
    if (
      dragStartY.current !== null &&
      currentY !== undefined &&
      currentY - dragStartY.current > DRAG_CLOSE_THRESHOLD
    ) {
      setOpen(false);
      dragStartY.current = null;
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <div
        data-testid="mobile-day-sheetly-controls"
        className="flex min-w-0 items-center gap-2"
      >
        <SheetTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label={`Date range: ${dateLabel}`}
            className="min-w-0 flex-1 justify-start gap-2 rounded-xl px-3 font-normal"
          >
            <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{dateLabel}</span>
          </Button>
        </SheetTrigger>
        <SheetTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label="Filters"
            data-testid="mobile-day-sheetly-filters-trigger"
            className="shrink-0 gap-2 rounded-xl px-3"
          >
            <Filter className="h-4 w-4" aria-hidden="true" />
            <span>Filters</span>
          </Button>
        </SheetTrigger>
      </div>

      <SheetContent
        side="bottom"
        aria-describedby="mobile-day-sheetly-filters-description"
        className="max-h-[82vh] overflow-y-auto rounded-t-3xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 sm:mx-auto sm:max-w-lg"
        closeButtonClassName={datePopoverOpen ? 'hidden' : undefined}
        closeButtonTestId="mobile-day-sheetly-filters-close"
        onInteractOutside={(event) => {
          if (
            event.target instanceof Element &&
            event.target.closest('[data-radix-popper-content-wrapper]')
          ) {
            event.preventDefault();
          }
        }}
      >
        <div
          data-testid="mobile-day-sheetly-filters-handle"
          className="mx-auto mb-3 h-1.5 w-12 touch-none rounded-full bg-muted-foreground/30"
          aria-hidden="true"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={(event) => closeFromDrag(event.clientY)}
          onPointerCancel={() => {
            dragStartY.current = null;
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={(event) => closeFromDrag(event.changedTouches[0]?.clientY)}
          onTouchCancel={() => {
            dragStartY.current = null;
          }}
        />
        <SheetTitle className="text-2xl font-semibold tracking-tight">Filters</SheetTitle>
        <SheetDescription id="mobile-day-sheetly-filters-description" className="sr-only">
          Filter the Day Sheetly timeline by date range and project
        </SheetDescription>

        <div
          ref={setPopoverContainer}
          className="mt-4 rounded-2xl border bg-card p-3"
        >
          <DaySheetlyFiltersComponent
            filters={draftFilters}
            defaultDateRange={defaultDateRange}
            onChange={setDraftFilters}
            projects={projects}
            showLabel={false}
            popoverContainer={popoverContainer}
            onDatePopoverOpenChange={setDatePopoverOpen}
          />
        </div>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <Button
            type="button"
            variant="outline"
            className="h-12 justify-center gap-2 rounded-xl"
            onClick={() => {
              handleOpenChange(false);
              onImport();
            }}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            Import JSON
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-12 justify-center gap-2 rounded-xl"
            onClick={() => {
              handleOpenChange(false);
              onExport();
            }}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Export JSON
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
